/**
 * SessionTimeout Component
 * Handles session timeout detection and user warnings with auto-logout functionality
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Alert } from '../ui/alert';

interface SessionTimeoutProps {
  warningTimeMinutes?: number; // Minutes before expiry to show warning
  inactivityMinutes?: number; // Minutes of inactivity before auto-logout
  checkIntervalSeconds?: number; // How often to check for timeout
  enableInactivityTimeout?: boolean;
  onTimeout?: () => void;
  onWarning?: () => void;
}

interface TimeoutState {
  showWarning: boolean;
  timeRemaining: number;
  isActive: boolean;
  lastActivity: Date;
}

export const SessionTimeout: React.FC<SessionTimeoutProps> = ({
  warningTimeMinutes = 5,
  inactivityMinutes = 30,
  checkIntervalSeconds = 30,
  enableInactivityTimeout = true,
  onTimeout,
  onWarning,
}) => {
  const { user, logout, refreshToken, updateActivity, getSessionInfo } = useAuth();

  const [timeoutState, setTimeoutState] = useState<TimeoutState>({
    showWarning: false,
    timeRemaining: 0,
    isActive: true,
    lastActivity: new Date(),
  });

  // Refs to persist values across re-renders
  const checkIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const activityListeners = useRef<boolean>(false);
  const lastActivityRef = useRef<Date>(new Date());

  /**
   * Calculate time remaining until session expiry
   */
  const calculateTimeRemaining = useCallback((): number => {
    if (!user || !enableInactivityTimeout) return Infinity;

    const sessionInfo = getSessionInfo();
    const lastActivity = sessionInfo.lastActivity || lastActivityRef.current;
    const inactivityTimeout = inactivityMinutes * 60 * 1000; // Convert to milliseconds
    const expiryTime = new Date(lastActivity.getTime() + inactivityTimeout);

    return Math.max(0, expiryTime.getTime() - Date.now());
  }, [user, inactivityMinutes, enableInactivityTimeout, getSessionInfo]);

  /**
   * Handle user activity to reset timeout
   */
  const handleActivity = useCallback(() => {
    lastActivityRef.current = new Date();
    updateActivity();

    setTimeoutState(prev => ({
      ...prev,
      isActive: true,
      lastActivity: lastActivityRef.current,
    }));

    // Hide warning if user becomes active
    if (timeoutState.showWarning) {
      setTimeoutState(prev => ({ ...prev, showWarning: false }));
    }
  }, [updateActivity, timeoutState.showWarning]);

  /**
   * Check for timeout and update state
   */
  const checkTimeout = useCallback(() => {
    if (!user || !enableInactivityTimeout) return;

    const remainingTime = calculateTimeRemaining();
    const warningThreshold = warningTimeMinutes * 60 * 1000; // Convert to milliseconds

    setTimeoutState(prev => ({
      ...prev,
      timeRemaining: remainingTime,
    }));

    // Show warning if approaching timeout
    if (remainingTime <= warningThreshold && remainingTime > 0 && !timeoutState.showWarning) {
      setTimeoutState(prev => ({ ...prev, showWarning: true }));

      if (onWarning) {
        onWarning();
      }
    }

    // Auto-logout if expired
    if (remainingTime <= 0) {
      handleTimeout();
    }
  }, [
    user,
    enableInactivityTimeout,
    calculateTimeRemaining,
    warningTimeMinutes,
    timeoutState.showWarning,
    onWarning,
  ]);

  /**
   * Handle session timeout
   */
  const handleTimeout = useCallback(async () => {
    setTimeoutState(prev => ({ ...prev, showWarning: false }));

    if (onTimeout) {
      onTimeout();
    }

    try {
      await logout();
    } catch (error) {
      console.error('Error during timeout logout:', error);
      // Force logout by redirecting to login
      window.location.href = '/login';
    }
  }, [logout, onTimeout]);

  /**
   * Extend session by refreshing token
   */
  const extendSession = useCallback(async () => {
    try {
      await refreshToken();
      handleActivity();
      setTimeoutState(prev => ({ ...prev, showWarning: false }));
    } catch (error) {
      console.error('Failed to extend session:', error);
      handleTimeout();
    }
  }, [refreshToken, handleActivity, handleTimeout]);

  /**
   * Setup activity listeners
   */
  const setupActivityListeners = useCallback(() => {
    if (activityListeners.current || !enableInactivityTimeout) return;

    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];

    const throttledHandleActivity = throttle(handleActivity, 1000);

    events.forEach(event => {
      document.addEventListener(event, throttledHandleActivity, true);
    });

    activityListeners.current = true;

    // Cleanup function
    return () => {
      events.forEach(event => {
        document.removeEventListener(event, throttledHandleActivity, true);
      });
      activityListeners.current = false;
    };
  }, [handleActivity, enableInactivityTimeout]);

  /**
   * Format time remaining for display
   */
  const formatTimeRemaining = (milliseconds: number): string => {
    const totalSeconds = Math.ceil(milliseconds / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;

    if (minutes > 0) {
      return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    }
    return `${seconds} seconds`;
  };

  // Setup effects
  useEffect(() => {
    if (!user || !enableInactivityTimeout) return;

    // Setup activity listeners
    const cleanup = setupActivityListeners();

    // Setup timeout checking interval
    checkIntervalRef.current = setInterval(checkTimeout, checkIntervalSeconds * 1000);

    // Initial check
    checkTimeout();

    return () => {
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
      }
      if (cleanup) {
        cleanup();
      }
    };
  }, [user, enableInactivityTimeout, setupActivityListeners, checkTimeout, checkIntervalSeconds]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
      }
    };
  }, []);

  // Don't render if user is not authenticated or timeout is disabled
  if (!user || !enableInactivityTimeout) {
    return null;
  }

  return (
    <>
      {/* Session Timeout Warning Dialog */}
      <Dialog open={timeoutState.showWarning} onOpenChange={() => {}}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Session Expiring Soon</DialogTitle>
            <DialogDescription>
              Your session will expire in{' '}
              <span className="font-medium text-red-600">
                {formatTimeRemaining(timeoutState.timeRemaining)}
              </span>
              {' '}due to inactivity.
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            <Alert>
              <div className="text-sm">
                To maintain security, your session will automatically end after {inactivityMinutes} minutes
                of inactivity. Click "Stay Signed In" to extend your session.
              </div>
            </Alert>
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={handleTimeout}
              className="w-full sm:w-auto"
            >
              Sign Out Now
            </Button>
            <Button
              onClick={extendSession}
              className="w-full sm:w-auto"
            >
              Stay Signed In
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Optional: Session status indicator */}
      {import.meta.env.DEV && (
        <div className="fixed bottom-4 right-4 bg-gray-800 text-white text-xs p-2 rounded opacity-50">
          Session: {formatTimeRemaining(timeoutState.timeRemaining)} remaining
        </div>
      )}
    </>
  );
};

/**
 * Throttle utility function
 */
function throttle<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let inThrottle: boolean;
  let lastFunc: NodeJS.Timeout;
  let lastRan: number;

  return function (this: any, ...args: Parameters<T>) {
    if (!inThrottle) {
      func.apply(this, args);
      lastRan = Date.now();
      inThrottle = true;
    } else {
      clearTimeout(lastFunc);
      lastFunc = setTimeout(() => {
        if (Date.now() - lastRan >= wait) {
          func.apply(this, args);
          lastRan = Date.now();
        }
      }, Math.max(wait - (Date.now() - lastRan), 0));
    }
  };
}

/**
 * Hook for session timeout functionality
 */
export const useSessionTimeout = (options: SessionTimeoutProps = {}) => {
  const [Component, setComponent] = useState<React.ReactElement | null>(null);

  useEffect(() => {
    setComponent(<SessionTimeout {...options} />);
  }, [options]);

  return Component;
};

export default SessionTimeout;